import {
  Card,
  CardContent,
  SpinnerButton,
  TextField,
  Theme,
  ValueAutocompleteField,
  createStyles,
  makeStyles,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { CompanyField } from 'components/shared/dropdowns'
import {
  CreateOrUpdateOpportunityMutationVariables,
  GetAllOpportunitiesDocument,
  InteractionTokenInput,
  ListingStructureType,
  MinimalOpportunitiesDocument,
  OpportunityCertainty,
  TradeIntentStatusType,
  TradeIntentType,
  useCreateOrUpdateOpportunityMutation,
} from 'graphql/bindings/AdminBindings'
import { DEFAULT_FEE } from 'util/PricingUtils'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { PercentField } from 'components/shared/PercentField'
import { TaggedConversation } from 'components/communication-input/CommunicationInput'
import { WrappedCommunicationInput } from 'components/shared/Communications'
import {
  disableEnterKeySubmission,
  parseSizeOrPrice,
  validateFeeField,
  validatePriceOrValuation,
  validateSizeOrSharesField,
} from 'util/FormUtils'
import { formatPercent, parsePercent } from 'util/NumberUtils'
import { formatPriceOrValuation, formatSharesOrSize } from 'util/ParsersFormatters'
import React from 'react'
import startCase from 'lodash/startCase'

interface OpportunityCreatorProps {
  opportunityId?: string
  accountId?: string
}

type FormShape = {
  type: TradeIntentType
  id?: string
  account_id?: string
  company_id?: { label: string; value: number }
  structure?: ListingStructureType[]
  size_or_shares?: string
  price_or_valuation?: string
  status?: TradeIntentStatusType
  latest_activity?: string
  fee?: string
  postToSlackChannel?: 'marketplace' | 'client_strategy' | 'marketplace_team'
  note?: TaggedConversation
  mutationResult?: MutationResult
}

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    firstRow: {
      display: 'flex',
      justifyContent: 'space-evenly',
      alignItems: 'top',
    },
    secondRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'top',
    },
    rowItem: {
      marginRight: spacing(2),
    },
    type: {
      marginRight: spacing(2),
      flex: '1 0',
    },
    issuer: {
      marginRight: spacing(2),
      flex: '1 0',
    },
    sizeOrPrice: {
      marginRight: spacing(2),
      flex: '1 0',
    },
    notes: {
      marginRight: spacing(2),
      flex: '3 0',
    },
    slackField: {
      marginRight: spacing(2),
      flex: '2 0',
    },
    fee: {
      marginRight: spacing(2),
      flex: '1 0',
    },
  })
)

const CreateOpportunityForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, values, valid }) => {
  const classes = useStyles()
  const mutateInProgress = values?.mutationResult?.loading ?? false

  return (
    <form onSubmit={handleSubmit} onKeyPress={disableEnterKeySubmission}>
      <div className={classes.firstRow}>
        <div className={classes.type}>
          <ValueAutocompleteField
            name="type"
            label="Type"
            options={[
              { label: 'Bid', value: TradeIntentType.Bid },
              { label: 'Ask', value: TradeIntentType.Ask },
            ]}
          />
        </div>

        <div className={classes.issuer}>
          <CompanyField name="company_id" label="Issuer" />
        </div>

        <div className={classes.sizeOrPrice}>
          <Field
            component={TextField}
            name="size_or_shares"
            label="Size/Shares"
            format={formatSharesOrSize}
            formatOnBlur
            fullWidth
          />
        </div>

        <div className={classes.sizeOrPrice}>
          <Field
            component={TextField}
            name="price_or_valuation"
            label="Price/Valuation"
            format={formatPriceOrValuation}
            formatOnBlur
            fullWidth
          />
        </div>

        <div className={classes.fee}>
          <PercentField name="fee" label="Fee" />
        </div>
      </div>
      <div className={classes.secondRow}>
        <div className={classes.notes}>
          <WrappedCommunicationInput name="note" label="Notes" accountId={values.account_id} />
        </div>

        <div className={classes.slackField}>
          <ValueAutocompleteField
            name="postToSlackChannel"
            label="Share to Slack Channel"
            options={[
              { value: 'marketplace', label: '#marketplace' },
              { value: 'client_strategy', label: '#client_strategy' },
              { value: 'marketplace_team', label: '#marketplace-team' },
            ]}
          />
        </div>

        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!valid}
          loading={mutateInProgress}
          caption={'Add'}
          size="large"
        />
      </div>
    </form>
  )
}

const OpportunityCreator: React.FC<OpportunityCreatorProps> = ({ accountId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [mutate, mutateResult] = useCreateOrUpdateOpportunityMutation({
    refetchQueries: [
      {
        query: MinimalOpportunitiesDocument,
        variables: { accountId },
      },
      {
        query: GetAllOpportunitiesDocument,
        variables: { accountId },
      },
    ],
  })

  const initialValues: FormShape = {
    type: TradeIntentType.Bid,
    account_id: accountId,
    postToSlackChannel: 'marketplace',
    mutationResult: mutateResult,
    fee: formatPercent(DEFAULT_FEE),
  }

  const handleSubmit = (values: FormShape) => {
    const fee = values?.fee !== undefined ? parsePercent(values.fee) : DEFAULT_FEE

    const parsedSizedValues = values.size_or_shares ? parseSizeOrPrice(values.size_or_shares, 'size', fee) : {}
    const parsedPriceValues = values.price_or_valuation ? parseSizeOrPrice(values.price_or_valuation, 'price', fee) : {}

    const variables: CreateOrUpdateOpportunityMutationVariables = {
      form: {
        type: values.type,
        account_id: accountId,
        company_id: values.company_id!.value,
        latest_activity: values?.latest_activity,
        renew: true,
        certainty: OpportunityCertainty.Theory,
        fee: fee,
        ...parsedSizedValues,
        ...parsedPriceValues,
        postToSlackChannel: values.postToSlackChannel,
        interaction:
          values.note && values.note.text !== ''
            ? {
                notes: values.note.text,
                tokens: values.note.tokens as InteractionTokenInput[],
                tagged_value: JSON.stringify(values.note.json),
              }
            : undefined,
      },
    }

    mutate({ variables }).then(() => {
      enqueueSnackbar('Created new opportunity')
    })
  }

  const validate = (values: FormShape) => {
    const { size_or_shares, price_or_valuation, type, fee } = values
    const requiredFields = ['company_id', 'type'] as string[]
    const valueObj = values as { [key: string]: any }

    const errors = {
      size_or_shares: validateSizeOrSharesField(size_or_shares),
      price_or_valuation: validatePriceOrValuation(price_or_valuation, type),
      fee: validateFeeField(fee),
    }

    return requiredFields.reduce((acc, el) => {
      if (!valueObj[el]) {
        const missingField = el === 'company_id' ? 'Issuer' : startCase(el)
        return { ...acc, [el]: `${missingField} is required` }
      }
      return acc
    }, errors)
  }

  return (
    <Card>
      <CardContent>
        <Form<FormShape>
          component={CreateOpportunityForm}
          onSubmit={handleSubmit}
          validate={validate}
          initialValues={{
            ...initialValues,
          }}
        />
      </CardContent>
    </Card>
  )
}

export { OpportunityCreator }
